import type {
  CalendarProvider,
  ChannelFlags,
  NotificationPreference,
  PreferenceEvent,
  TelegramLinkCode,
} from '@pm/schemas';
import { displayPhone, PREFERENCE_EVENT_LABEL } from '@pm/schemas';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  ErrorState,
  Field,
  Input,
  LabelCaps,
  PageHeader,
  Skeleton,
  Tabs,
} from '@pm/ui';
import {
  Bell,
  CalendarSync,
  Check,
  Copy,
  ExternalLink,
  KeyRound,
  Link2,
  Mail,
  MessageCircle,
  Send,
  Trash2,
  Unlink,
} from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import {
  intKeys,
  useCalendarIntegrations,
  useChannels,
  useInvalidating,
  useNotificationPreferences,
} from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { copyText } from '@/features/forms/form-bits';

const EVENT_LABEL = PREFERENCE_EVENT_LABEL;

const CHANNELS: { key: keyof ChannelFlags; label: string; icon: typeof Bell }[] = [
  { key: 'in_app', label: 'No app', icon: Bell },
  { key: 'email', label: 'E-mail', icon: Mail },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { key: 'telegram', label: 'Telegram', icon: Send },
];

/** NOTIFICATIONS §3, §5, §7 — preferências por evento × canal, vínculos e calendários externos. */
export function NotificationsSettingsPage() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState<'prefs' | 'channels' | 'calendar'>(
    params.get('calendar') ? 'calendar' : 'prefs',
  );
  return (
    <div className="flex max-w-[960px] flex-col gap-gap-lg">
      <PageHeader
        title="Notificações"
        subtitle="Escolha por evento onde quer ser avisado. O aviso no app sempre existe; os canais externos são adicionais."
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'prefs', label: 'Preferências' },
          { value: 'channels', label: 'Canais' },
          { value: 'calendar', label: 'Calendário externo' },
        ]}
      />
      {tab === 'prefs' ? (
        <PreferencesGrid />
      ) : tab === 'channels' ? (
        <ChannelsSection />
      ) : (
        <CalendarSection />
      )}
    </div>
  );
}

function PreferencesGrid() {
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useNotificationPreferences();
  const { data: channels } = useChannels();
  const save = useInvalidating(
    (items: NotificationPreference[]) =>
      api.put<NotificationPreference[]>('/me/notification-preferences', { items }),
    [intKeys.prefs],
  );
  if (isLoading) return <Skeleton className="h-[320px] rounded-md" />;
  if (isError || !data)
    return <ErrorState message={error?.message} onRetry={() => void refetch()} />;
  const toggle = (ev: PreferenceEvent, key: keyof ChannelFlags) => {
    const row = data.find((p) => p.event_type === ev)!;
    save.mutate([{ ...row, [key]: !row[key] }], { onError: (e) => toast.error(e) });
  };
  const setAll = (key: keyof ChannelFlags, value: boolean) =>
    save.mutate(
      data.map((p) => ({ ...p, [key]: value })),
      { onSuccess: () => toast.success('Preferências salvas'), onError: (e) => toast.error(e) },
    );
  const unavailable = (key: keyof ChannelFlags) =>
    key === 'email'
      ? !channels?.email.available
      : key === 'whatsapp'
        ? !channels?.whatsapp.available || !channels.whatsapp.number
        : key === 'telegram'
          ? !channels?.telegram.available || !channels.telegram.linked
          : false;

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-surface-1">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
            <th className="h-9 px-3 text-left font-medium">Evento</th>
            {CHANNELS.map((c) => (
              <th key={c.key} className="h-9 px-2 text-center font-medium">
                <span className="inline-flex items-center gap-1">
                  <c.icon className="size-3.5" /> {c.label}
                </span>
                {c.key !== 'in_app' ? (
                  <div className="mt-0.5 flex justify-center gap-1 normal-case tracking-normal">
                    <button
                      type="button"
                      className="text-[10px] text-text-3 hover:text-text-1"
                      onClick={() => setAll(c.key, true)}
                    >
                      todos
                    </button>
                    <span className="text-[10px] text-text-3">·</span>
                    <button
                      type="button"
                      className="text-[10px] text-text-3 hover:text-text-1"
                      onClick={() => setAll(c.key, false)}
                    >
                      nenhum
                    </button>
                  </div>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((p) => (
            <tr
              key={p.event_type}
              className="h-row border-b border-border last:border-b-0 hover:bg-surface-2"
            >
              <td className="px-3 text-sm">
                {EVENT_LABEL[p.event_type]}
                <span className="ml-2 font-mono text-[10px] text-text-3">{p.event_type}</span>
              </td>
              {CHANNELS.map((c) => (
                <td key={c.key} className="px-2 text-center">
                  <Checkbox
                    checked={p[c.key]}
                    disabled={c.key === 'in_app' || save.isPending}
                    onCheckedChange={() => toggle(p.event_type, c.key)}
                    aria-label={`${EVENT_LABEL[p.event_type]} via ${c.label}`}
                    className={unavailable(c.key) && p[c.key] ? 'opacity-50' : undefined}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="border-t border-border px-3 py-2 text-xs text-text-3">
        Canais marcados mas não vinculados (ou não configurados nesta instalação) ficam registrados
        como ignorados. Vincule em <strong>Canais</strong>.
      </p>
    </div>
  );
}

function ChannelsSection() {
  const toast = useToast();
  const { data, isLoading } = useChannels();
  const [number, setNumber] = useState<string | null>(null);
  const [code, setCode] = useState<TelegramLinkCode | null>(null);
  const [unlink, setUnlink] = useState(false);
  const setWhats = useInvalidating(
    (n: string | null) => api.put('/me/channels/whatsapp', { number: n }),
    [intKeys.channels],
  );
  const genCode = useInvalidating(
    () => api.post<TelegramLinkCode>('/me/channels/telegram/link-code'),
    [intKeys.channels],
  );
  const unlinkTg = useInvalidating(() => api.delete('/me/channels/telegram'), [intKeys.channels]);
  if (isLoading || !data) return <Skeleton className="h-[240px] rounded-md" />;
  const whatsValue = number ?? data.whatsapp.number ?? '';

  return (
    <div className="grid gap-gap-lg lg:grid-cols-3">
      <section className="flex flex-col gap-2 rounded-md border border-border bg-surface-1 p-4">
        <div className="flex items-center gap-2">
          <Mail className="size-4 text-text-3" />
          <LabelCaps>E-mail</LabelCaps>
          <Availability ok={data.email.available} />
        </div>
        <p className="font-mono text-sm">{data.email.address ?? '—'}</p>
        <p className="text-xs text-text-3">
          Usa o e-mail da sua conta. Peça a um administrador para alterá-lo.
        </p>
      </section>

      <section className="flex flex-col gap-2 rounded-md border border-border bg-surface-1 p-4">
        <div className="flex items-center gap-2">
          <MessageCircle className="size-4 text-text-3" />
          <LabelCaps>WhatsApp</LabelCaps>
          <Availability ok={data.whatsapp.available} />
        </div>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setWhats.mutate(whatsValue.trim() || null, {
              onSuccess: () => (toast.success('Número salvo'), setNumber(null)),
              onError: (err) => toast.error(err),
            });
          }}
        >
          <Input
            mono
            value={whatsValue}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="+5511999990000"
            className="h-control-sm text-sm"
          />
          <Button type="submit" variant="secondary" size="sm" loading={setWhats.isPending}>
            <Check />
          </Button>
        </form>
        {!data.whatsapp.number && data.whatsapp.profile_number ? (
          <p className="text-xs text-text-2">
            Usando o telefone do seu perfil:{' '}
            <span className="font-mono text-text-1">
              {displayPhone(`+${data.whatsapp.profile_number}`)}
            </span>
            . Preencha acima só se o WhatsApp for outro número.
          </p>
        ) : !data.whatsapp.number ? (
          <p className="text-xs text-warning">
            Sem número: as mensagens não são enviadas. Informe aqui ou cadastre um telefone em Meu
            perfil.
          </p>
        ) : null}
        <p className="text-xs text-text-3">
          Formato internacional, só dígitos. As mensagens saem pela Evolution API da organização.
        </p>
      </section>

      <section className="flex flex-col gap-2 rounded-md border border-border bg-surface-1 p-4">
        <div className="flex items-center gap-2">
          <Send className="size-4 text-text-3" />
          <LabelCaps>Telegram</LabelCaps>
          <Availability ok={data.telegram.available} />
        </div>
        {data.telegram.linked ? (
          <>
            <p className="flex items-center gap-2 text-sm">
              <Badge tone="success">vinculado</Badge>
              <span className="font-mono text-xs text-text-3">chat {data.telegram.chat_id}</span>
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="self-start text-danger"
              onClick={() => setUnlink(true)}
            >
              <Unlink /> Desvincular
            </Button>
          </>
        ) : code ? (
          <>
            <p className="text-sm">
              Envie ao bot{' '}
              {data.telegram.bot_username ? (
                <a
                  href={code.deep_link ?? '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-accent hover:underline"
                >
                  @{data.telegram.bot_username}
                </a>
              ) : null}
              :
            </p>
            <div className="flex items-center gap-2">
              <code className="rounded bg-surface-2 px-2 py-1 font-mono text-sm">
                /start {code.code}
              </code>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Copiar"
                onClick={() =>
                  void copyText(`/start ${code.code}`).then((ok) => ok && toast.success('Copiado'))
                }
              >
                <Copy />
              </Button>
              {code.deep_link ? (
                <a
                  className="inline-flex size-7 items-center justify-center rounded text-text-2 hover:bg-surface-2"
                  href={code.deep_link}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Abrir no Telegram"
                >
                  <ExternalLink className="size-4" />
                </a>
              ) : null}
            </div>
            <p className="text-xs text-text-3">
              Válido até {fmtDateTime(code.expires_at)}. A página atualiza sozinha quando o bot
              confirmar.
            </p>
          </>
        ) : (
          <>
            <p className="text-xs text-text-3">
              Gere um código e envie ao bot da organização para vincular este chat.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="self-start"
              disabled={!data.telegram.available}
              loading={genCode.isPending}
              onClick={() =>
                genCode.mutate(undefined, { onSuccess: setCode, onError: (e) => toast.error(e) })
              }
            >
              <Link2 /> Gerar código
            </Button>
          </>
        )}
        <ConfirmDialog
          open={unlink}
          onOpenChange={setUnlink}
          title="Desvincular Telegram?"
          description="Você deixa de receber notificações por lá até vincular de novo."
          confirmLabel="Desvincular"
          danger
          loading={unlinkTg.isPending}
          onConfirm={() =>
            unlinkTg.mutate(undefined, {
              onSuccess: () => (
                toast.success('Telegram desvinculado'),
                setUnlink(false),
                setCode(null)
              ),
              onError: (e) => toast.error(e),
            })
          }
        />
      </section>
    </div>
  );
}

function Availability({ ok }: { ok: boolean }) {
  return (
    <Badge tone={ok ? 'success' : 'neutral'} className="ml-auto">
      {ok ? 'disponível' : 'não configurado'}
    </Badge>
  );
}

const PROVIDER_LABEL: Record<CalendarProvider, string> = {
  GOOGLE: 'Google Calendar',
  MICROSOFT: 'Microsoft 365 / Outlook',
  CALDAV: 'CalDAV (cPanel, Nextcloud, iCloud…)',
};

function CalendarSection() {
  const toast = useToast();
  const { data, isLoading } = useCalendarIntegrations();
  const [caldav, setCaldav] = useState<'cpanel' | 'generic' | null>(null);
  const [remove, setRemove] = useState<string | null>(null);
  const connect = useInvalidating(
    (p: 'google' | 'microsoft') =>
      api.post<{ url: string }>(`/me/calendar-integrations/${p}/connect`),
    [],
  );
  const disconnect = useInvalidating(
    (id: string) => api.delete(`/me/calendar-integrations/${id}`),
    [intKeys.calendars],
  );
  if (isLoading || !data) return <Skeleton className="h-[240px] rounded-md" />;

  return (
    <div className="flex flex-col gap-gap">
      <p className="text-sm text-text-2">
        Os compromissos do Calendário em que você participa são espelhados na sua agenda (sistema →
        externo). Entregas (prazos de demandas) ficam só no WIP. Alterações feitas lá fora não
        voltam nesta versão.
      </p>
      <div className="rounded-md border border-border bg-surface-1">
        {data.items.length === 0 ? (
          <p className="p-6 text-center text-sm text-text-3">Nenhum calendário conectado.</p>
        ) : (
          <ul>
            {data.items.map((it) => (
              <li
                key={it.id}
                className="flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-b-0"
              >
                <CalendarSync className="size-4 text-text-3" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm">
                    <span className="font-medium">{PROVIDER_LABEL[it.provider]}</span>
                    {it.account_label ? (
                      <span className="ml-2 text-text-2">{it.account_label}</span>
                    ) : null}
                  </p>
                  <p className="font-mono text-xs text-text-3">
                    {it.status === 'ERROR' ? (
                      <span className="text-danger">erro: {it.last_error}</span>
                    ) : it.last_synced_at ? (
                      `sincronizado ${fmtDateTime(it.last_synced_at)}`
                    ) : (
                      'aguardando primeiro evento'
                    )}
                  </p>
                </div>
                <Badge
                  tone={
                    it.status === 'ACTIVE'
                      ? 'success'
                      : it.status === 'ERROR'
                        ? 'danger'
                        : 'neutral'
                  }
                >
                  {it.status}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Desconectar"
                  onClick={() => setRemove(it.id)}
                >
                  <Trash2 />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={!data.available.GOOGLE}
          onClick={() =>
            connect.mutate('google', {
              onSuccess: (r) => window.location.assign(r.url),
              onError: (e) => toast.error(e),
            })
          }
        >
          Conectar Google{' '}
          {!data.available.GOOGLE ? (
            <span className="font-mono text-[10px] text-text-3">não configurado</span>
          ) : null}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!data.available.MICROSOFT}
          onClick={() =>
            connect.mutate('microsoft', {
              onSuccess: (r) => window.location.assign(r.url),
              onError: (e) => toast.error(e),
            })
          }
        >
          Conectar Microsoft{' '}
          {!data.available.MICROSOFT ? (
            <span className="font-mono text-[10px] text-text-3">não configurado</span>
          ) : null}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setCaldav('cpanel')}>
          <KeyRound /> Conectar cPanel
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setCaldav('generic')}>
          <KeyRound /> Outro CalDAV
        </Button>
      </div>
      <Dialog open={caldav !== null} onOpenChange={(o) => !o && setCaldav(null)}>
        <DialogContent
          title={caldav === 'cpanel' ? 'Conectar agenda do cPanel' : 'Conectar calendário CalDAV'}
          description="A senha é guardada cifrada e usada só para gravar compromissos na sua agenda."
          size="sm"
        >
          {caldav ? (
            <CalDavForm key={caldav} preset={caldav} onDone={() => setCaldav(null)} />
          ) : null}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={remove !== null}
        onOpenChange={() => setRemove(null)}
        title="Desconectar calendário?"
        description="Eventos já espelhados permanecem lá; novos não serão mais enviados."
        confirmLabel="Desconectar"
        danger
        loading={disconnect.isPending}
        onConfirm={() =>
          remove &&
          disconnect.mutate(remove, {
            onSuccess: () => (toast.success('Calendário desconectado'), setRemove(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

/**
 * cPanel ("Calendários e contatos"): o servidor CalDAV fica na porta 2080 (SSL) e a agenda
 * padrão em /calendars/<e-mail>/calendar; o login é o próprio e-mail e a senha dele.
 */
function cpanelUrl(host: string, email: string): string {
  const h = host
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/[:/].*$/, '');
  return h && email.trim() ? `https://${h}:2080/calendars/${email.trim()}/calendar` : '';
}

function CalDavForm({ preset, onDone }: { preset: 'cpanel' | 'generic'; onDone: () => void }) {
  const toast = useToast();
  const cpanel = preset === 'cpanel';
  const [host, setHost] = useState('');
  const [rawUrl, setUrl] = useState('');
  const [rawUsername, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [label, setLabel] = useState('');
  const url = cpanel ? cpanelUrl(host, rawUsername) : rawUrl;
  const username = rawUsername.trim();
  const save = useInvalidating(
    (input: Record<string, unknown>) => api.post('/me/calendar-integrations/caldav', input),
    [intKeys.calendars],
  );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          { url, username, password, account_label: label || undefined },
          {
            onSuccess: () => (toast.success('Calendário conectado'), onDone()),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      {cpanel ? (
        <Field
          label="Servidor"
          htmlFor="cd-host"
          hint="O mesmo do webmail ou do cPanel, ex.: mail.suaempresa.com.br"
        >
          <Input
            id="cd-host"
            mono
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder="mail.suaempresa.com.br"
          />
        </Field>
      ) : (
        <Field
          label="URL da coleção"
          htmlFor="cd-url"
          hint="Ex.: https://nextcloud.exemplo.com/remote.php/dav/calendars/ana/personal/"
        >
          <Input
            id="cd-url"
            type="url"
            mono
            value={rawUrl}
            onChange={(e) => setUrl(e.target.value)}
          />
        </Field>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={cpanel ? 'E-mail' : 'Usuário'} htmlFor="cd-user">
          <Input
            id="cd-user"
            type={cpanel ? 'email' : 'text'}
            value={rawUsername}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
          />
        </Field>
        <Field label={cpanel ? 'Senha do e-mail' : 'Senha / app password'} htmlFor="cd-pass">
          <Input
            id="cd-pass"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Field>
      </div>
      {cpanel && url ? (
        <p className="break-all font-mono text-xs text-text-3">
          {url}
          <span className="mt-1 block font-sans">
            Se a sua agenda no cPanel tiver outro nome, use &quot;Outro CalDAV&quot; com o endereço
            mostrado em Calendários e contatos.
          </span>
        </p>
      ) : null}
      <Field label="Rótulo (opcional)" htmlFor="cd-label">
        <Input
          id="cd-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Agenda pessoal"
        />
      </Field>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={save.isPending}
          disabled={!url || !username || !password}
        >
          Conectar
        </Button>
      </DialogFooter>
    </form>
  );
}
